/** @jsxImportSource @emotion/react */
/**
 * ActionGrid — Visual card grid for selecting event template
 * Groups templates by supercategories (Наблюдения / Уход)
 */
import React from 'react';
import { css } from '@emotion/react';
import { Spin, Typography, Empty } from 'antd';
import type { EventTemplate } from '../../types/eventTemplates';
import { useTranslation } from 'react-i18next';
import { SUPERCATEGORIES, categoryLabel, groupTemplatesBySupercategory, supercategoryLabel } from './ActionWizard.types';

const { Text } = Typography;

const styles = {
  container: css({
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
  }),
  supercategorySection: css({
    borderRadius: 8,
    padding: '12px 16px',
  }),
  supercategoryLabel: css({
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 10,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  }),
  grid: css({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 10,
  }),
  card: css({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
    padding: '16px 10px',
    borderRadius: 10,
    border: '2px solid transparent',
    background: '#fff',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    minHeight: 90,
    textAlign: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  }),
  cardSelected: css({
    borderColor: '#1890ff',
    background: '#e6f7ff',
    boxShadow: '0 2px 8px rgba(24,144,255,0.25)',
  }),
  cardIcon: css({
    fontSize: 28,
    lineHeight: 1,
  }),
  cardName: css({
    fontSize: 13,
    fontWeight: 500,
    lineHeight: 1.3,
    color: '#333',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical' as any,
  }),
  cardCustomTag: css({
    fontSize: 10,
    color: '#8c8c8c',
    background: '#f5f5f5',
    padding: '1px 6px',
    borderRadius: 4,
  }),
  centerSpin: css({
    display: 'flex',
    justifyContent: 'center',
    padding: 40,
  }),
};

/** Default icons for templates missing an icon */
const DEFAULT_ICONS: Record<string, string> = {
  inspection: '🔍',
  measurement: '📏',
  treatment: '🧪',
  care: '✂️',
  maintenance: '💧',
  harvest: '🌾',
  lifecycle: '🌱',
};

interface ActionGridProps {
  templates: EventTemplate[];
  selectedTemplateId: number | null;
  onSelect: (template: EventTemplate) => void;
  loading?: boolean;
  /** In simulation mode hide Observations (inspection/measurement) — simulator fills those automatically */
  isSimMode?: boolean;
}

const ActionGrid: React.FC<ActionGridProps> = ({
  templates,
  selectedTemplateId,
  onSelect,
  loading = false,
  isSimMode = false,
}) => {
  const { t } = useTranslation('events');
  if (loading) {
    return (
      <div css={styles.centerSpin}>
        <Spin tip={t('actionGrid.loadingTemplates')} />
      </div>
    );
  }

  const visibleSupercategories = isSimMode
    ? SUPERCATEGORIES.filter((sc) => sc.key !== 'observation')
    : SUPERCATEGORIES;
  const grouped = groupTemplatesBySupercategory(templates);
  const hasAny = templates.length > 0;

  if (!hasAny) {
    return <Empty description={t('actionGrid.noTemplates')} />;
  }

  return (
    <div css={styles.container}>
      {visibleSupercategories.map((sc) => {
        const scTemplates = grouped.get(sc.key) ?? [];
        if (scTemplates.length === 0) return null;

        return (
          <div
            key={sc.key}
            css={[styles.supercategorySection, css({ background: sc.color, border: `1px solid ${sc.borderColor}` })]}
          >
            <div css={styles.supercategoryLabel}>
              <span>{sc.icon}</span>
              <span>{supercategoryLabel(sc.key)}</span>
              <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
                ({sc.categories.map((category) => categoryLabel(category)).join(', ')})
              </Text>
            </div>
            <div css={styles.grid}>
              {scTemplates.map((template) => {
                const isSelected = selectedTemplateId === template.id;
                const icon = template.icon || DEFAULT_ICONS[template.category] || '📋';

                return (
                  <div
                    key={template.id}
                    css={[styles.card, isSelected && styles.cardSelected]}
                    onClick={() => onSelect(template)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onSelect(template);
                      }
                    }}
                  >
                    <span css={styles.cardIcon}>{icon}</span>
                    <span css={styles.cardName}>{template.name}</span>
                    {template.scopeType === 'farm' && (
                      <span css={styles.cardCustomTag}>{t('actionGrid.custom')}</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default ActionGrid;
